/**
 * Pure H OS · Phase D Wave 3 · Meetings.
 * Recurring + one-off meetings across the executive team.
 */
import { useMemo, useState } from "react";
import { Card, SectionHeader, SeverityBadge } from "../components/lib";
import { TabBar, ListRow, Drawer } from "../components/lib.overlay";
import { MEETINGS, MeetingKind, MeetingStatus } from "../mocks/meetingsData";

const KIND_LABEL: Record<MeetingKind, string> = {
  weekly: "Weekly", monthly: "Monthly", quarterly: "Quarterly",
  "1on1": "1:1", adhoc: "Ad-hoc",
};
function statusSeverity(s: MeetingStatus): "low" | "medium" | "high" {
  return s === "scheduled" ? "medium" : "low";
}

type Tab = "all" | MeetingKind;

export default function Meetings() {
  const [tab, setTab] = useState<Tab>("all");
  const [openId, setOpenId] = useState<string | null>(null);

  const filtered = useMemo(() => (tab === "all" ? MEETINGS : MEETINGS.filter((m) => m.kind === tab)), [tab]);
  const current = openId ? MEETINGS.find((m) => m.id === openId) ?? null : null;

  return (
    <div className="space-y-6" data-testid="meetings-page">
      <Card testId="mtg-filters">
        <TabBar<Tab>
          testId="mtg-tabs"
          active={tab}
          onChange={setTab}
          tabs={[
            { key: "all",       label: `All ${MEETINGS.length}` },
            { key: "weekly",    label: "Weekly" },
            { key: "monthly",   label: "Monthly" },
            { key: "quarterly", label: "Quarterly" },
            { key: "1on1",      label: "1:1" },
            { key: "adhoc",     label: "Ad-hoc" },
          ]}
        />
      </Card>

      <Card padded={false} testId="mtg-list">
        <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <SectionHeader eyebrow="Meetings" title={`${filtered.length} meeting${filtered.length === 1 ? "" : "s"}`} />
          <div className="text-xs" style={{ color: "var(--ph-muted)" }}>Executive cadence · decisions and action items tracked per meeting.</div>
        </div>
        <div className="p-2">
          {filtered.map((m) => (
            <ListRow key={m.id} testId={`mtg-${m.id}`}
              onClick={() => setOpenId(m.id)}
              label={
                <span className="flex items-center gap-2">
                  <span className="uppercase tracking-[0.14em] text-[10px]" style={{ color: "var(--ph-muted)" }}>{KIND_LABEL[m.kind]}</span>
                  <span>{m.title}</span>
                </span>
              }
              sublabel={<>{m.scheduledRelative} · {m.attendees.length} attendees · {m.decisionsMade} decisions · {m.actionItems} actions</>}
              trailing={<SeverityBadge severity={statusSeverity(m.status)} />}
            />
          ))}
        </div>
      </Card>

      <Drawer open={!!current} onClose={() => setOpenId(null)} testId="mtg-drawer" title={current ? current.title : ""}>
        {current && (
          <div className="space-y-6">
            <SectionHeader eyebrow={`${KIND_LABEL[current.kind]} · ${current.status}`} title={current.owner} />
            <div className="text-xs -mt-4" style={{ color: "var(--ph-muted)" }}>{current.scheduledRelative}</div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Attendees</div>
              <div className="mt-1 text-sm" style={{ color: "var(--ph-text)" }}>{current.attendees.join(", ")}</div>
            </div>
            {current.agenda && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Agenda</div>
                <div className="mt-1 text-sm leading-relaxed whitespace-pre-wrap" style={{ color: "var(--ph-text)" }} data-testid={`mtg-agenda-${current.id}`}>{current.agenda}</div>
              </div>
            )}
            {current.minutes && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Minutes</div>
                <div className="mt-1 text-sm leading-relaxed whitespace-pre-wrap" style={{ color: "var(--ph-text)" }} data-testid={`mtg-minutes-${current.id}`}>{current.minutes}</div>
              </div>
            )}
            {current.actionItemsList && current.actionItemsList.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Action items</div>
                <ul className="mt-1 text-sm space-y-1 list-disc pl-5" style={{ color: "var(--ph-text)" }} data-testid={`mtg-actions-${current.id}`}>
                  {current.actionItemsList.map((a, i) => <li key={i}>{a}</li>)}
                </ul>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Decisions made</div>
                <div className="mt-1 text-sm tabular-nums" style={{ color: "var(--ph-text)" }}>{current.decisionsMade}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Action items</div>
                <div className="mt-1 text-sm tabular-nums" style={{ color: "var(--ph-text)" }}>{current.actionItems}</div>
              </div>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}
